import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';
import type { JournalPhraseVisibility } from '@/utils/journalPhrases';

export type JournalPhraseRow = Database['public']['Tables']['journal_phrases']['Row'];

export const journalPhrasesKey = (clinicId: string | null | undefined) =>
  ['journal', 'phrases', clinicId ?? null] as const;

export function useJournalPhrases(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: journalPhrasesKey(clinicId),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<JournalPhraseRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('journal_phrases')
        .select('*')
        .eq('clinic_id', clinicId)
        .order('shortcut', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 30_000,
  });
}

export interface JournalPhraseInput {
  shortcut: string;
  title?: string | null;
  body: string;
  visibility: JournalPhraseVisibility;
}

function invalidatePhrases(queryClient: ReturnType<typeof useQueryClient>, clinicId: string) {
  void queryClient.invalidateQueries({ queryKey: journalPhrasesKey(clinicId) });
}

export function useCreateJournalPhrase(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: JournalPhraseInput): Promise<JournalPhraseRow> => {
      if (!clinicId) throw new Error('No active clinic');
      const { data, error } = await supabase
        .from('journal_phrases')
        .insert({
          clinic_id: clinicId,
          shortcut: input.shortcut.trim(),
          title: input.title?.trim() || null,
          body: input.body,
          visibility: input.visibility,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      if (clinicId) invalidatePhrases(queryClient, clinicId);
    },
  });
}

export function useUpdateJournalPhrase(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: JournalPhraseInput & { id: string }): Promise<JournalPhraseRow> => {
      if (!clinicId) throw new Error('No active clinic');
      const { data, error } = await supabase
        .from('journal_phrases')
        .update({
          shortcut: input.shortcut.trim(),
          title: input.title?.trim() || null,
          body: input.body,
          visibility: input.visibility,
        })
        .eq('id', input.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      if (clinicId) invalidatePhrases(queryClient, clinicId);
    },
  });
}

export function useDeleteJournalPhrase(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!clinicId) throw new Error('No active clinic');
      const { error } = await supabase.from('journal_phrases').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      if (clinicId) invalidatePhrases(queryClient, clinicId);
    },
  });
}
